/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import partition from 'lodash/partition';
import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import { type Command } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import Modal, {
	CloseButton,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Flex, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { toggleSetting } from '../../../../pm-plugins/ai-proactive/commands';
import { SETTING_KEYS } from '../../../../pm-plugins/ai-proactive/constants';
import { getPluginState as getProactivePluginState } from '../../../../pm-plugins/ai-proactive/plugin-factory';
import {
	getSettingsWithValues,
	type SuggestionSettingView,
} from '../../../../pm-plugins/ai-proactive/suggestion-setting-map';

import { SettingItem } from './setting-item';

const styles = cssMap({
	grid: {
		width: '100%',
		gridTemplateAreas: 'title close',
	},

	closeContainer: {
		gridArea: 'close',
	},

	titleContainer: {
		gridArea: 'title',
	},

	stickyHeaderStyle: {
		position: 'sticky',
		top: token('space.0'),
		zIndex: 1,
	},

	scrollableSuggestionStyle: {
		overflow: 'hidden auto',
		flexShrink: 1,
	},

	divider: {
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
	},
});

type SuggestionSettingProps = {
	isOpen: boolean;
	toggleModal: () => void;
	editorView: EditorView;
};

const Divider = () => {
	return <Box xcss={styles.divider} />;
};

export const SuggestionSetting = (props: SuggestionSettingProps) => {
	const { isOpen, toggleModal, editorView } = props;

	const { formatMessage } = useIntl();

	const runCommand = (command: Command) => {
		const { state, dispatch } = editorView;
		command(state, dispatch);
	};

	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const proactivePluginState = getProactivePluginState(editorView!.state);

	const { settings } = proactivePluginState;

	const { displayAllSuggestions } = settings;

	const suggestionSettings = getSettingsWithValues(settings);

	const [allSuggestionSetting, otherSuggestionSettings] = partition(
		suggestionSettings,
		(item: SuggestionSettingView) => item.settingKey === SETTING_KEYS.ALL_SUGGESTIONS,
	);

	const renderSettings = (settings: SuggestionSettingView[]) => {
		return settings.map(
			({ defaultValue, description, display, Icon, indent, settingKey, title }) => (
				<SettingItem
					key={settingKey}
					icon={<Icon color={token('color.icon.subtle')} />}
					title={formatMessage(title)}
					description={description ? formatMessage(description) : undefined}
					defaultValue={defaultValue}
					toggle={() => {
						runCommand(toggleSetting(settingKey));
					}}
					display={display}
					indented={indent}
				/>
			),
		);
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={toggleModal}>
					<ModalHeader>
						<Grid gap="space.200" xcss={styles.grid}>
							<Flex xcss={styles.closeContainer} justifyContent="end">
								<CloseButton onClick={toggleModal} />
							</Flex>
							<Flex xcss={styles.titleContainer} justifyContent="start">
								<ModalTitle>{formatMessage(aiProactiveMessages.ManageSuggestionTitle)}</ModalTitle>
							</Flex>
						</Grid>
					</ModalHeader>
					<Box xcss={styles.scrollableSuggestionStyle}>
						<Box xcss={styles.stickyHeaderStyle} backgroundColor="elevation.surface">
							<Box paddingInline="space.300">{renderSettings(allSuggestionSetting)}</Box>
							{displayAllSuggestions ? <Divider /> : null}
						</Box>
						<Box paddingInline="space.300" paddingBlockEnd="space.500">
							{renderSettings(otherSuggestionSettings)}
						</Box>
					</Box>
				</Modal>
			)}
		</ModalTransition>
	);
};
